import { useState } from "react";
import {
  Globe,
  Search,
  ChevronDown,
  User,
  Settings,
  LogOut,
  Mail,
  Plug,
  Layers,
  KeyRound,
  Check,
  Menu,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/theme-toggle";
import { useLogout } from "@/hooks/useLogout";
import { useAuth } from "@/contexts/AuthContext";
import { getInitials } from "@/lib/account-profile-utils";
import { ChangePasswordDialog } from "@/components/account/ChangePasswordDialog";
import NotificationBell from "@/components/shared/notifications/NotificationBell";

const languages = [
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "hi", label: "हिन्दी", flag: "🇮🇳" },
];

const pageTitles: Record<string, string> = {
  "/admin": "Dashboard",
  "/admin/stores": "Store Management",
  "/admin/products": "Product Approvals",
  "/admin/categories": "Categories",
  "/admin/orders": "Order Management",
  "/admin/users": "User Management",
  "/admin/distributors": "Distributors",
  "/admin/wholesalers": "Wholesalers",
  "/admin/retailers": "Retailers",
  "/admin/channel-products": "Channel products",
  "/admin/channel-products/new": "Add Channel Product",
  "/admin/channel-orders": "Channel orders",
  "/admin/delivery": "Delivery Partners",
  "/admin/wallet": "Wallet & Payouts",
  "/admin/wallet/transactions": "Wallet Transactions",
  "/admin/reports": "Reports",
  "/admin/income/self": "Self Cashback",
  "/admin/income/level": "Level Income",
  "/admin/income/reward": "Reward Income",
  "/admin/settings": "Settings",
  "/admin/notifications": "Notifications",
  "/admin/banners": "Banners",
  "/admin/cms": "CMS Pages",
  "/admin/coupons": "Coupons",
  "/admin/referral-levels": "Referral Levels",
  "/admin/email-settings": "Email Settings",
  "/admin/integrations": "Integrations",
  "/admin/profile": "My Profile",
};

interface AdminTopbarProps {
  collapsed: boolean;
  onToggleSidebar: () => void;
  onMobileMenuToggle: () => void;
}

const AdminTopbar = ({ collapsed, onToggleSidebar, onMobileMenuToggle }: AdminTopbarProps) => {
  const logout = useLogout("/auth/admin/secure/login");
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [searchFocused, setSearchFocused] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const location = useLocation();
  const currentPageTitle =
    pageTitles[location.pathname] ??
    (location.pathname.startsWith("/admin/reports/") ? "Reports" : null) ??
    (location.pathname.startsWith("/admin/settings/") ? "Settings" : null) ??
    (location.pathname.match(/^\/admin\/stores\/\d+$/)
      ? "Store Details"
      : location.pathname === "/admin/products/new"
        ? "Add Product"
        : location.pathname.match(/^\/admin\/channel-products\/\d+\/edit$/)
          ? "Edit Channel Product"
          : location.pathname.match(/^\/admin\/products\/\d+\/edit$/)
            ? "Edit Product"
          : location.pathname.match(/^\/admin\/products\/\d+$/)
            ? "Product Details"
            : location.pathname.match(/^\/admin\/delivery\/\d+$/)
              ? "Driver Details"
              : location.pathname.match(/^\/admin\/wallet\/users\//)
                ? "User Wallet"
                : "Admin");
  const currentLang = i18n.language || "en";
  const selectedLang = languages.find((l) => l.code === currentLang) || languages[0];

  const displayName = user?.name?.trim() || "Admin";
  const displayEmail = user?.email ?? "";
  const initials = getInitials(displayName);

  return (
    <>
      <header className="glass-bar sticky top-0 z-20 h-16 shrink-0">
        <div className="flex h-full items-center justify-between gap-2 pl-1 pr-2 sm:pl-2 sm:pr-3 lg:pl-3 lg:pr-5">
          <div className="flex min-w-0 items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="glass-chip h-10 w-10 shrink-0 rounded-xl p-0 text-foreground hover:bg-transparent hover:text-foreground focus:bg-transparent active:bg-transparent"
              onClick={() => {
                if (window.innerWidth < 1024) {
                  onMobileMenuToggle();
                } else {
                  onToggleSidebar();
                }
              }}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <Menu className="h-5 w-5 stroke-[2.25]" />
            </Button>

            <div className="hidden min-w-0 items-center gap-1.5 text-sm md:flex">
              <span className="text-muted-foreground text-xs">Admin</span>
              <span className="text-muted-foreground/70 text-xs">/</span>
              <span className="truncate font-semibold text-foreground text-xs sm:text-sm">{currentPageTitle}</span>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-0.5 sm:gap-1.5">
            <div
              className={cn(
                "hidden lg:flex items-center relative transition-all duration-300",
                searchFocused ? "w-[280px]" : "w-[200px]",
              )}
            >
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search..."
                className="h-9 pl-9 pr-3 text-xs text-foreground bg-secondary/60 border-transparent rounded-lg focus-visible:bg-card focus-visible:border-border focus-visible:ring-1 focus-visible:ring-primary/20 transition-all placeholder:text-muted-foreground"
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
              />
            </div>

            <ThemeToggle />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="glass-chip h-9 gap-1 rounded-xl px-2 text-muted-foreground hover:bg-transparent hover:text-foreground">
                  <Globe className="h-4 w-4" />
                  <span className="hidden sm:inline text-[11px] font-semibold">{selectedLang.code.toUpperCase()}</span>
                  <ChevronDown className="h-3 w-3 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" collisionPadding={8} className="w-[180px] bg-popover border border-border shadow-xl z-50 rounded-xl p-1">
                <DropdownMenuLabel className="text-[10px] text-muted-foreground uppercase tracking-wider px-2">Language</DropdownMenuLabel>
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => {
                      i18n.changeLanguage(lang.code);
                      localStorage.setItem("language", lang.code);
                    }}
                    className={cn(
                      "cursor-pointer gap-2.5 rounded-lg text-sm",
                      currentLang === lang.code && "bg-primary/10 text-primary font-medium",
                    )}
                  >
                    <span className="text-base leading-none">{lang.flag}</span>
                    <span className="flex-1">{lang.label}</span>
                    {currentLang === lang.code && <Check className="h-3.5 w-3.5" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <NotificationBell scope="admin" viewAllPath="/admin/notifications" />

            <Separator orientation="vertical" className="h-7 hidden sm:block mx-0.5" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="glass-chip group h-10 gap-2 rounded-xl px-1.5 hover:bg-transparent sm:px-2.5">
                  <div className="relative">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-primary text-primary-foreground text-xs font-bold ring-2 ring-primary/20 group-hover:ring-primary/40 transition-all">
                      {initials}
                    </div>
                    <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-success border-2 border-card" />
                  </div>
                  <div className="hidden lg:flex flex-col items-start leading-tight">
                    <span className="text-[13px] font-semibold text-foreground max-w-[120px] truncate">{displayName}</span>
                    <span className="text-[10px] text-muted-foreground font-medium">{t("profile.administrator")}</span>
                  </div>
                  <ChevronDown className="h-3 w-3 text-muted-foreground hidden sm:block group-hover:text-foreground transition-colors" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={8} collisionPadding={8} className="w-[min(250px,calc(100vw-1rem))] bg-popover border border-border shadow-2xl z-50 rounded-xl p-1.5 overflow-hidden">
                <div className="px-2.5 py-3 mb-1 rounded-lg bg-secondary/40">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-primary text-primary-foreground text-sm font-bold">
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold truncate text-foreground">{displayName}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{displayEmail}</p>
                    </div>
                  </div>
                </div>
                <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg text-sm py-2" asChild>
                  <Link to="/admin/profile">
                    <User className="h-4 w-4 text-muted-foreground" /> {t("profile")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="my-1" />
                <DropdownMenuLabel className="text-[10px] text-muted-foreground uppercase tracking-wider px-2">
                  {t("settings")}
                </DropdownMenuLabel>
                <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg text-sm py-2" asChild>
                  <Link to="/admin/settings/platform">
                    <Settings className="h-4 w-4 text-muted-foreground" /> {t("nav.settings")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg text-sm py-2" asChild>
                  <Link to="/admin/settings/email">
                    <Mail className="h-4 w-4 text-muted-foreground" /> {t("nav.email")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg text-sm py-2" asChild>
                  <Link to="/admin/settings/integrations">
                    <Plug className="h-4 w-4 text-muted-foreground" /> {t("nav.integrations")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg text-sm py-2" asChild>
                  <Link to="/admin/settings/levels">
                    <Layers className="h-4 w-4 text-muted-foreground" /> {t("nav.referrals")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="my-1" />
                <DropdownMenuItem
                  className="cursor-pointer gap-2.5 rounded-lg text-sm py-2"
                  onClick={() => setPasswordOpen(true)}
                >
                  <KeyRound className="h-4 w-4 text-muted-foreground" /> {t("profile.change_password")}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="my-1" />
                <DropdownMenuItem
                  className="cursor-pointer gap-2.5 rounded-lg text-sm py-2 text-destructive focus:text-destructive focus:bg-destructive/10"
                  onClick={() => logout()}
                >
                  <LogOut className="h-4 w-4" /> {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} />
    </>
  );
};

export default AdminTopbar;
